<template>
    <div class="FileQuestion">
        <div class="FileQuestion__instructions">
            {{ description }} Supported file formats include <var>{{ humanReadableExtensions }}</var>.
        </div>
        <label
            class="FileDropbox"
            :class="{ draghover: dragHover }"
            :for="id"
            @click="openFileBrowser"
            @dragover="onDragOver"
            @dragleave="onDragLeave"
            @dragend="onDragLeave"
            @drop="onDrop"
        >
            <slot>
                <span class="FileDropbox__icon"><Icon name="upload" /></span>
                <span class="file-dropbox-text">Drag a file here or click to browse.</span>
            </slot>
        </label>
        <input
            :id="id"
            ref="input"
            type="file"
            multiple
            :name="id"
            :accept="extensions.map(x => `.${x}`).join(',')"
            style="display: none"
            @change="onChange"
        >
        <FilesListPreview
            v-if="modelValue && modelValue.length > 0"
            :files="modelValue"
            :uploading="submitting"
            @remove="removeFile"
        />
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from 'vue-property-decorator';

    import { cloneFile } from '@evidentid/file-utils/blobs';
    import { extensions, regularExtensions, regularMimeTypes, mimeTypes } from '@evidentid/file-utils/mimeTypes';
    import { validateFiles } from '@evidentid/file-utils/prepareFile';

    import Icon from '@/components/common/Icon.vue';

    import FilesListPreview from './FilesListPreview.vue';

    // PRODUCT-26110 Task 12 (COMPONENT_V_MODEL): reached via FileUploadInput.vue's
    // `v-bind="forwardedProps"` spread, which includes a literal `modelValue` key --
    @Component({
        inheritAttrs: false,
        components: { Icon, FilesListPreview },
        emits: [ 'update:modelValue', 'invalid' ],
    })
    export default class DesktopFileUploadInput extends Vue {
        @Prop({ type: String, default: undefined })
        private id!: string | undefined;

        @Prop({ type: Array, default: () => [] })
        private modelValue!: File[];

        @Prop({ type: Boolean, default: false })
        private submitting!: boolean;

        @Prop({ type: Boolean, default: false })
        private imagesOnly!: boolean;

        @Prop({ type: String, default: '' })
        private description!: string;

        private dragHover: boolean = false;

        private get extensions() {
            return this.imagesOnly ? extensions.image : regularExtensions.filter((extension) => extension !== 'txt');
        }

        private get mimeTypes() {
            return this.imagesOnly ? mimeTypes.image : regularMimeTypes.filter((type) => type !== 'text/plain');
        }

        private get humanReadableExtensions() {
            const upperCase = this.extensions.map((extension: string) => extension.toUpperCase());
            return `${upperCase.slice(0, -1).join(', ')}, and ${upperCase[upperCase.length - 1]}`;
        }

        private openFileBrowser(event: Event) {
            event.preventDefault();

            const input = this.$refs.input as HTMLInputElement;

            if (input) {
                input.click();
            }
        }

        private removeFile(file: File) {
            this.$emit('update:modelValue', (this.modelValue || []).filter((x) => x !== file));
        }

        private async addFiles(files: FileList | File[]) {
            const { success, failed } = validateFiles(files, this.mimeTypes);
            // PRODUCT-11363: Chrome can discard previous `File` instances, when it will encounter new exactly same one
            const successCopy = await Promise.all(success.map(cloneFile));
            this.$emit('update:modelValue', (this.modelValue || []).concat(successCopy));
            if (failed.length > 0) {
                this.$emit('invalid', failed);
            }
        }

        private onDragOver(event: Event) {
            event.preventDefault();
            this.dragHover = true;
        }

        private onDragLeave(event: Event) {
            event.preventDefault();
            this.dragHover = false;
        }

        private onDrop(event: Event) {
            event.preventDefault();
            this.dragHover = false;

            const { files } = ((event as DragEvent).dataTransfer as DataTransfer) || (event.target as HTMLInputElement);
            this.addFiles(files);
        }

        private onChange(event: Event) {
            this.onDrop(event);

            if (event.target) {
                (event.target as HTMLInputElement).value = '';
            }
        }
    }
</script>
